/**
 * Compact pagination:
 * - Always: first 4 pages, middle 4 pages (centered on round(total / 2)), last 4 pages.
 * - Optional 4th window: only if current page is not inside any of those three blocks
 *   (pages around current, same window as typical “around current” strips).
 * - Overlapping windows are merged; ellipses separate non-overlapping segments (including
 *   after the first block even when the next page is 5).
 */

export type CompactPaginationItem =
  | { kind: 'page'; n: number }
  | { kind: 'ellipsis' };

type Segment = { start: number; end: number };

function clampPage(p: number, total: number): number {
  return Math.min(Math.max(1, Math.floor(p)), total);
}

function middleSegment(total: number): Segment {
  const center = Math.round(total / 2);
  let midStart = Math.max(5, center - 1);
  let midEnd = midStart + 3;
  if (midEnd > total) {
    midEnd = total;
    midStart = Math.max(1, midEnd - 3);
  }
  return { start: midStart, end: midEnd };
}

function currentSegmentIfNeeded(total: number, current: number, start: Segment, mid: Segment, end: Segment): Segment | null {
  const c = clampPage(current, total);
  const inStart = c >= start.start && c <= start.end;
  const inMid = c >= mid.start && c <= mid.end;
  const inEnd = c >= end.start && c <= end.end;
  if (inStart || inMid || inEnd) {
    return null;
  }
  let cs = Math.max(5, Math.min(c - 2, total - 3));
  const ce = Math.min(cs + 3, total);
  if (cs > ce) {
    return null;
  }
  return { start: cs, end: ce };
}

/** Merge only overlapping segments (start <= last.end); keep adjacent segments separate. */
function mergeOverlappingSorted(segments: Segment[]): Segment[] {
  const sorted = [...segments].sort((a, b) => a.start - b.start);
  const merged: Segment[] = [];
  for (const seg of sorted) {
    if (merged.length === 0) {
      merged.push({ ...seg });
      continue;
    }
    const last = merged[merged.length - 1];
    if (seg.start <= last.end) {
      last.end = Math.max(last.end, seg.end);
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

function segmentsToItems(segments: Segment[]): CompactPaginationItem[] {
  const out: CompactPaginationItem[] = [];
  for (let i = 0; i < segments.length; i++) {
    if (i > 0) {
      out.push({ kind: 'ellipsis' });
    }
    const { start, end } = segments[i];
    for (let p = start; p <= end; p++) {
      out.push({ kind: 'page', n: p });
    }
  }
  return out;
}

export function buildCompactPaginationItems(
  total: number,
  current: number,
  options?: { showAllMax?: number },
): CompactPaginationItem[] {
  const showAllMax = options?.showAllMax ?? 14;
  if (total <= 1) {
    return [];
  }
  if (total <= showAllMax) {
    return Array.from({ length: total }, (_, i) => ({ kind: 'page' as const, n: i + 1 }));
  }

  const startSeg: Segment = { start: 1, end: Math.min(4, total) };
  const endSeg: Segment = { start: Math.max(1, total - 3), end: total };
  const midSeg = middleSegment(total);
  const currSeg = currentSegmentIfNeeded(total, current, startSeg, midSeg, endSeg);

  const blocks: Segment[] = [startSeg, midSeg, endSeg];
  if (currSeg) {
    blocks.push(currSeg);
  }

  const merged = mergeOverlappingSorted(blocks);
  return segmentsToItems(merged);
}
